/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { objectSpread2 as _objectSpread2 } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Button, Tag } from '@carbon/react';
import { Close } from '@carbon/react/icons';
import { useTranslation } from '../../../../Utilities/i18n/i18n.js';
import { emptyObject, noopFn } from '../../../../Utilities/propHelpers/defaultProps.js';
import { match } from '../../../../Utilities/keyboard/match.js';
import { Enter } from '../../../../Utilities/keyboard/keys.js';

var renderPanel = function renderPanel(args) {
  var children = args.children,
    disablePrimaryButton = args.disablePrimaryButton,
    disabled = args.disabled,
    onClearAllFilters = args.onClearAllFilters,
    onCommitFilters = args.onCommitFilters,
    onCloseFilterPanel = args.onCloseFilterPanel,
    mode = args.mode,
    t = args.t;
  var childrenWithProps = React.Children.toArray(React.Children.toArray(children)).map(function (child) {
    return /*#__PURE__*/React.cloneElement(child, {
      mode: mode
    });
  });
  var onClear = function onClear() {
    var preservedFilterKeys = childrenWithProps.filter(function (child) {
      return child.props.disabled;
    }).map(function (child) {
      return child.props.columnKey;
    });
    onClearAllFilters(preservedFilterKeys);
    onCloseFilterPanel();
  };
  var okLabel = mode === 'batch' ? t('Apply') : t('Done');
  var onOk = function onOk() {
    if (mode === 'batch') {
      onCommitFilters();
    }
    onCloseFilterPanel();
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "cds--table-toolbar-filter-panel"
  }, childrenWithProps, /*#__PURE__*/React.createElement("div", {
    className: "cds--filters-button-bar"
  }, /*#__PURE__*/React.createElement(Button, {
    disabled: disabled || childrenWithProps.every(function (child) {
      return child.props.disabled;
    }),
    iconDescription: "Clear",
    kind: "secondary",
    size: "md",
    type: "button",
    onClick: onClear
  }, t('Clear')), /*#__PURE__*/React.createElement(Button, {
    disabled: disablePrimaryButton || disabled,
    iconDescription: okLabel,
    kind: "primary",
    size: "md",
    type: "button",
    onClick: onOk
  }, okLabel)));
};
var createClearSelection = function createClearSelection(_ref) {
  var filter = _ref.filter,
    selectionId = _ref.selectionId,
    filterSelections = _ref.filterSelections,
    onFilterChange = _ref.onFilterChange;
  return function () {
    var args = {
      columnKey: filter
    };
    if (Array.isArray(filterSelections[filter])) {
      var _filterSelections$fil, _filterSelections$fil2;
      args.selectedItems = (_filterSelections$fil = (_filterSelections$fil2 = filterSelections[filter]).filter) === null || _filterSelections$fil === void 0 ? void 0 : _filterSelections$fil.call(_filterSelections$fil2, function (item) {
        return item.id !== selectionId;
      });
    } else if (typeof filterSelections[filter] === 'string') {
      args.value = '';
    }
    // update the filter in both the 'live' and 'batch' states
    onFilterChange(_objectSpread2(_objectSpread2({}, args), {}, {
      mode: 'live'
    }));
    onFilterChange(_objectSpread2(_objectSpread2({}, args), {}, {
      mode: 'batch'
    }));
  };
};
var renderSelectionTags = function renderSelectionTags(args) {
  var children = args.children,
    headers = args.headers,
    filterSelections = args.filterSelections,
    onFilterChange = args.onFilterChange,
    onClearAllFilters = args.onClearAllFilters,
    t = args.t,
    disabled = args.disabled;
  var headerLabels = headers.reduce(function (acc, curr) {
    var key = curr.key,
      header = curr.header;
    acc[key] = header;
    return acc;
  }, {});
  var childrenWithProps = React.Children.toArray(React.Children.toArray(children));
  var preservedFilterKeys = childrenWithProps.filter(function (child) {
    return child.props.disabled;
  }).map(function (child) {
    return child.props.columnKey;
  });
  var onClearKeyDown = function onClearKeyDown(evt) {
    if (match(evt, Enter)) {
      onClearAllFilters(preservedFilterKeys);
    }
  };
  var tags = Object.keys(filterSelections).map(function (filter) {
    var selections = filterSelections[filter];
    if (typeof filterSelections[filter] === 'string') {
      var input = filterSelections[filter];
      selections = input ? [input] : [];
    }
    return selections.map(function (selection) {
      var _selection$id, _selection$label, _childrenWithProps$fi;
      var id = (_selection$id = selection.id) !== null && _selection$id !== void 0 ? _selection$id : selection;
      var label = (_selection$label = selection.label) !== null && _selection$label !== void 0 ? _selection$label : selection;
      var disabledTag = disabled || ((_childrenWithProps$fi = childrenWithProps.find(function (child) {
        return child.props.columnKey === filter;
      })) === null || _childrenWithProps$fi === void 0 ? void 0 : _childrenWithProps$fi.props.disabled);
      return /*#__PURE__*/React.createElement(Tag, {
        key: "".concat(filter, "-").concat(id),
        filter: true,
        disabled: disabledTag,
        type: "gray",
        onClick: disabledTag ? undefined : createClearSelection({
          filter: filter,
          selectionId: id,
          filterSelections: filterSelections,
          onFilterChange: onFilterChange
        }),
        onClose: disabledTag ? undefined : createClearSelection({
          filter: filter,
          selectionId: id,
          filterSelections: filterSelections,
          onFilterChange: onFilterChange
        })
      }, "".concat(headerLabels && headerLabels[filter] || filter, ":").concat(label));
    });
  }).reduce(function (acc, cur) {
    return acc.concat(cur);
  }, []); // flatten array

  var disabledClearButton = disabled || tags.every(function (tag) {
    return tag.props.disabled;
  });
  return /*#__PURE__*/React.createElement("div", {
    className: "cds--table-toolbar-filter-tags"
  }, /*#__PURE__*/React.createElement("div", null, tags), tags.length > 0 && /*#__PURE__*/React.createElement(Button, {
    kind: "ghost",
    className: "cds--filters-clear",
    iconDescription: t('Clear filters'),
    tooltipAlignment: "center",
    tooltipPosition: "bottom",
    renderIcon: Close,
    onClick: function onClick() {
      return onClearAllFilters(preservedFilterKeys);
    },
    onKeyDown: onClearKeyDown,
    hasIconOnly: true,
    disabled: disabledClearButton
  }));
};
var TableToolbarFilterPanel = function TableToolbarFilterPanel(_ref2) {
  var isOpen = _ref2.isOpen,
    _ref2$mode = _ref2.mode,
    mode = _ref2$mode === void 0 ? 'live' : _ref2$mode,
    headers = _ref2.headers,
    _ref2$filterSelection = _ref2.filterSelections,
    filterSelections = _ref2$filterSelection === void 0 ? emptyObject : _ref2$filterSelection,
    onFilterChange = _ref2.onFilterChange,
    onClearAllFilters = _ref2.onClearAllFilters,
    _ref2$onCommitFilters = _ref2.onCommitFilters,
    onCommitFilters = _ref2$onCommitFilters === void 0 ? noopFn : _ref2$onCommitFilters,
    onCloseFilterPanel = _ref2.onCloseFilterPanel,
    children = _ref2.children,
    disablePrimaryButton = _ref2.disablePrimaryButton,
    disabled = _ref2.disabled;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;

  // close filter panel when clicked outside data table
  useEffect(function () {
    var eventTypes = ['mousedown', 'touchstart'];
    var handler = function handler(evt) {
      var _evt$target$className, _evt$target$className2;
      if (evt && evt.target && evt.target.isConnected && !evt.target.closest('.cds--table-with-filters') && evt.target.className !== 'cds--list-box__menu-item__option' && !((_evt$target$className = (_evt$target$className2 = evt.target.className).startsWith) !== null && _evt$target$className !== void 0 && _evt$target$className.call(_evt$target$className2, 'flatpickr')) && evt.target.className !== 'arrowUp' && evt.target.className !== 'arrowDown' && !evt.target.closest('.flatpickr-months')) {
        onCloseFilterPanel();
      }
    };
    eventTypes.forEach(function (evtType) {
      return document.addEventListener(evtType, handler);
    });
    return function () {
      eventTypes.forEach(function (evtType) {
        return document.removeEventListener(evtType, handler);
      });
    };
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, isOpen && renderPanel({
    children: children,
    onClearAllFilters: onClearAllFilters,
    onCommitFilters: onCommitFilters,
    onCloseFilterPanel: onCloseFilterPanel,
    mode: mode,
    disablePrimaryButton: disablePrimaryButton,
    disabled: disabled,
    t: t
  }), !isOpen && renderSelectionTags({
    children: children,
    headers: headers,
    filterSelections: filterSelections,
    onFilterChange: onFilterChange,
    onClearAllFilters: onClearAllFilters,
    disabled: disabled,
    t: t
  }));
};
TableToolbarFilterPanel.propTypes = {
  /**
   * Provide `isFilterPanelOpen` from render prop
   */
  isOpen: PropTypes.bool.isRequired,
  /**
   * Provide filter mode, either "live" or "batch".
   */
  mode: PropTypes.oneOf(['live', 'batch']),
  /**
   * Provide a set of all possible headers. This includes visible and hidden columns (if table settings feature is used). Do not use `headers` from the render prop as it only contains visible headers.
   */
  headers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  /**
   * Provide `filterSelections` from render prop
   */
  filterSelections: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.string])),
  /**
   * Provide `onFilterChange` from render prop
   */
  onFilterChange: PropTypes.func.isRequired,
  /**
   * Provide `onClearAllFilters` from render prop
   */
  onClearAllFilters: PropTypes.func.isRequired,
  /**
   * Provide `onCommitFilters` from render prop
   */
  onCommitFilters: PropTypes.func,
  /**
   * Provide `onCloseFilterPanel` from render prop
   */
  onCloseFilterPanel: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  /**
   * Provide an optional flag to enable or disable the primary button in the filter panel. This may be useful when you have custom validation logic and you wish to disable the primary button when validation fails.
   */
  disablePrimaryButton: PropTypes.bool,
  /**
   * Disable the ability to update any of the filters.
   */
  disabled: PropTypes.bool
};
var TableToolbarFilterPanel$1 = TableToolbarFilterPanel;

export { TableToolbarFilterPanel$1 as default };
